import styled from '@emotion/styled';
import { Icon, Select } from 'antd';
import moment from 'moment';
import React, { InputHTMLAttributes, useState } from 'react';
import Flag from 'react-world-flags';
import { DateInput } from 'semantic-ui-calendar-react';

import { color, font } from 'src/shared/styles/variables';
import { ColorVariant } from './ColorVariant';
import { OutlinedLabel } from './OutlinedLabel';

interface ColorProps {
    variant?: ColorVariant;
}

// Date Picker
const DatePicker = styled<any>(DateInput)<ColorProps>(({ variant }) => {
    let borderColor = '';
    switch (variant) {
        case ColorVariant.Error:
            borderColor = color.wildWatermelon;
            break;
        case ColorVariant.Active:
            borderColor = color.cornflowerBlueDark;
            break;
        default:
            borderColor = color.catskillWhite;
    }
    return {
        '&&.field .ui.icon.input': {
            width: '100%',
            i: {
                fontSize: font.size.medium,
                opacity: 1,
                color: color.blueBayoux,
            },
            input: {
                cursor: 'pointer',
                padding: 12,
                color: color.ebonyClay,
                lineHeight: '24px',
                borderStyle: 'solid',
                borderWidth: '2px',
                borderColor,
                ...font.roman(font.size.medium),
            },
        },
    };
});

// Select
const StyledSelect = styled(Select)<ColorProps>(({ variant }) => {
    let borderColor = '';
    switch (variant) {
        case ColorVariant.Error:
            borderColor = color.wildWatermelon;
            break;
        case ColorVariant.Active:
            borderColor = color.cornflowerBlueDark;
            break;
        default:
            borderColor = color.catskillWhite;
    }
    return {
        width: '100%',
        '> div': {
            lineHeight: '24px',
            height: '52px !important',
            borderStyle: 'solid',
            borderWidth: '2px',
            borderColor,
            ':hover': {
                borderColor,
            },
            ':focus': {
                borderColor,
                boxShadow: 'none',
            },
            '> span i svg': {
                color: color.blueBayoux,
            },
            '> div div': {
                paddingTop: '4px',
                color: color.ebonyClay,
                ...font.roman(font.size.medium),
            },
        },
    };
});
const OptionWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
});
const OptionText = styled.span(
    {
        color: color.ebonyClay,
    },

    font.roman(font.size.medium),
);
const FlagText = styled(OptionText)({
    marginBottom: -3,
    marginLeft: 14,
});
const SelectText = styled(OptionText)({
    color: color.ebonyClay,
});

// InputField
const InputField = styled.input<ColorProps>(({ variant }) => {
    let borderColor = '';
    switch (variant) {
        case ColorVariant.Error:
            borderColor = color.wildWatermelon;
            break;
        case ColorVariant.Active:
            borderColor = color.cornflowerBlueDark;
            break;
        default:
            borderColor = color.catskillWhite;
    }
    return {
        width: '100%',
        borderRadius: '4px',
        padding: '12px',
        lineHeight: '24px',
        borderStyle: 'solid',
        borderWidth: '2px',
        borderColor,
        color: color.ebonyClay,
        outline: 'none',
        ':focus': {
            borderColor,
        },
        ...font.roman(font.size.medium),
    };
});

// Textfield
const TextField = styled.textarea<ColorProps>(({ variant }) => {
    let borderColor = '';
    switch (variant) {
        case ColorVariant.Error:
            borderColor = color.wildWatermelon;
            break;
        case ColorVariant.Active:
            borderColor = color.cornflowerBlueDark;
            break;
        default:
            borderColor = color.catskillWhite;
    }
    return {
        width: '100%',
        height: '150px',
        padding: '12px',
        borderRadius: '4px',
        borderStyle: 'solid',
        borderWidth: '2px',
        borderColor,
        color: color.ebonyClay,
        outline: 'none',
        ':focus': {
            borderColor,
        },
        ...font.roman(font.size.medium),
    };
});

// Error message
const ErrorMessage = styled.div(
    {
        color: color.wildWatermelon,
        paddingTop: '4px',
        textAlign: 'left',
    },
    font.roman(font.size.base),
);

// General input container
const InputContainer = styled.div({ position: 'relative', width: '100%' });

// Main input component
export enum InputType {
    DatePicker = 'date-picker',
    TextField = 'text',
    TextArea = 'text-area',
    SingleSelect = 'single-select',
    CountrySelect = 'country-select',
    Password = 'password',
}
interface Props extends InputHTMLAttributes<HTMLInputElement> {
    fieldId: string;
    type: InputType;
    label: string;
    testID?: string;
    options?: { text: string; value: string; isoCode?: string }[];
    error?: string;
}
export const Input: React.FC<Props> = ({
    fieldId,
    type,
    value,
    onChange,
    onBlur,
    onFocus,
    label,
    error = '',
    testID = '',
    readOnly,
    options = [],
}) => {
    const handleChange = changedValue => {
        if (!changedValue) {
            setFloatingLabelFixed(true);
        }
        onChange?.(changedValue);
    };
    const handleDateChange = (_e, data: { value: string }) => {
        const pattern: RegExp = /^[0-9-]*$/;
        if (pattern.test(data.value)) {
            handleChange(data.value);
        }
    };

    const [active, setActive] = useState(false);
    const [floatingLabelFixed, setFloatingLabelFixed] = useState(true);
    const inputOnBlur = e => {
        setActive(false);
        setFloatingLabelFixed(false);
        onBlur?.(e);
    };
    const inputOnFocus = (e?) => {
        setActive(true);
        setFloatingLabelFixed(false);
        onFocus?.(e);
    };
    const minDate = moment().subtract(90, 'years');
    const maxDate = moment().subtract(10, 'years');
    const colorVariant = !!error
        ? ColorVariant.Error
        : active
        ? ColorVariant.Active
        : ColorVariant.Default;

    const labelId = `${fieldId}--Label`;
    const errorId = `${fieldId}--ErrorMessage`;

    return (
        <>
            <InputContainer>
                <OutlinedLabel
                    id={labelId}
                    label={label}
                    active={active}
                    floatingLabelFixed={floatingLabelFixed}
                    filled={!!value}
                    variant={colorVariant}
                />
                {(() => {
                    switch (type) {
                        case InputType.DatePicker:
                            return (
                                <DatePicker
                                    id={fieldId}
                                    aria-labelledby={labelId}
                                    name="date"
                                    dateFormat="YYYY-MM-DD"
                                    onBlur={inputOnBlur}
                                    onFocus={inputOnFocus}
                                    initialDate="1990-01-01"
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    value={value}
                                    onChange={handleDateChange}
                                    autoComplete="off"
                                    closable
                                    data-testid={testID}
                                    icon="calendar outline"
                                    variant={colorVariant}
                                    closeOnMouseLeave={false}
                                />
                            );
                        case InputType.SingleSelect:
                            return (
                                <StyledSelect
                                    aria-labelledby={labelId}
                                    size="large"
                                    onBlur={inputOnBlur}
                                    onFocus={inputOnFocus}
                                    value={value}
                                    onChange={handleChange}
                                    suffixIcon={<Icon type="caret-down" />}
                                    variant={colorVariant}
                                    data-testid={testID}
                                >
                                    {options.map(option => (
                                        <Select.Option key={option.text} value={option.value}>
                                            <OptionWrapper>
                                                <SelectText>{option.text}</SelectText>
                                            </OptionWrapper>
                                        </Select.Option>
                                    ))}
                                </StyledSelect>
                            );
                        case InputType.CountrySelect:
                            return (
                                <StyledSelect
                                    size="large"
                                    onBlur={inputOnBlur}
                                    onFocus={inputOnFocus}
                                    value={value}
                                    onChange={handleChange}
                                    suffixIcon={<Icon type="caret-down" />}
                                    variant={colorVariant}
                                    data-testid={testID}
                                >
                                    {options.map(option => (
                                        <Select.Option key={option.value} value={option.value}>
                                            <OptionWrapper>
                                                <Flag
                                                    code={option.isoCode}
                                                    width="36"
                                                    height="24"
                                                />
                                                <FlagText>{option.text}</FlagText>
                                            </OptionWrapper>
                                        </Select.Option>
                                    ))}
                                </StyledSelect>
                            );
                        case InputType.TextArea:
                            return (
                                <TextField
                                    id={fieldId}
                                    aria-labelledby={labelId}
                                    aria-describedby={error ? errorId : undefined}
                                    aria-invalid={!!error}
                                    name={fieldId}
                                    value={value}
                                    variant={colorVariant}
                                    onChange={e => handleChange(e.target.value)}
                                    onBlur={inputOnBlur}
                                    onFocus={inputOnFocus}
                                    data-testid={testID}
                                />
                            );
                        default:
                            return (
                                <InputField
                                    aria-labelledby={labelId}
                                    aria-describedby={error ? errorId : undefined}
                                    aria-invalid={!!error}
                                    type={type}
                                    id={fieldId}
                                    name={fieldId}
                                    value={value}
                                    onBlur={inputOnBlur}
                                    onFocus={inputOnFocus}
                                    variant={colorVariant}
                                    onChange={e => handleChange(e.target.value)}
                                    readOnly={readOnly}
                                    data-testid={testID}
                                />
                            );
                    }
                })()}

                {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
            </InputContainer>
        </>
    );
};
